import {
    useState, useRef, useEffect, useMemo,
} from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';

import Mic from '@mui/icons-material/Mic';
import InfoIcon from '@mui/icons-material/Info';
import { marked } from 'marked';
import sanitizeHtml from 'sanitize-html';
import GetAppIcon from '@mui/icons-material/GetApp';
import {
    ClickAwayListener,
    MenuItem, MenuList, Paper, Popper,
} from '@mui/material';

import { App } from 'BMapsSrc/BMapsApp';
import { Loader } from 'BMapsSrc/Loader';
import { captureAndTranscribe } from 'BMapsSrc/util/speech_utils';
import { DialogSubmitButton, DialogCancelButton } from './common/DialogActionButtons';
import ReturnedText from './common/ReturnedText';
import { WebServices } from '../WebServices';
import {
    interpretText, logGibbs, resumeInterpretText, stopWorkflow,
} from '../cmds/gibbsDispatch';
import { WorkingIndicator, downloadData } from './ui_utils';
import { loginUrlForMapCase } from '../connection_utils';
import BMapsTooltip from './BMapsTooltip';
import useInputHistory from './common/helpers/useInputHistory';
import { createInputHistory } from './common/inputHistory';

const BotName = 'Gibbs';

// Messages sent from the chat field, typed or spoken, for up/down recall. Answers given on a
// question card are not recorded: they only mean something to the question they answer.
// It lives here rather than in ChatBot so it outlives a remount of the help panel.
// Kept in sessionStorage: it survives a reload, but not the tab, since prompts can name
// proprietary compounds.
export const chatInputHistory = createInputHistory({
    storageKey: 'bmaps_chat_input_history_v1',
    storage: 'sessionStorage',
});

export function ChatBot({ loggedIn, initialMessages: initialMsgs=[] }) {
    const initialMessages = useMemo(
        () => initialMsgs.map((msg, i) => ({ ...msg, id: i })),
        [initialMsgs]
    );
    const [input, setInput] = useState('');
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const messageFieldRef = useRef(null);
    const [messages, setMessages] = useState(initialMessages);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [recording, setRecording] = useState(false);
    // Whether a workflow's steps are running, so it can be stopped.
    const [workflowRunning, setWorkflowRunning] = useState(false);
    const { onKeyDown: onHistoryKeyDown, reset: resetRecall } = useInputHistory({
        getEntries: chatInputHistory.getEntries, value: input, setValue: setInput, multiline: true,
    });

    useEffect(() => {
        if (messagesEndRef?.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [messages]);

    useEffect(() => {
        if (initialMessages.length > 0) {
            (async () => {
                try {
                    setIsLoading(true);
                    await submitMessages(initialMessages);
                } catch (sendError) {
                    console.error('Error making chat agent request:', sendError);
                    setError(sendError);
                } finally {
                    setIsLoading(false);
                }
            })();
        }
    }, [initialMessages]); // eslint-disable-line react-hooks/exhaustive-deps

    const myStructure = App.Workspace && App.Workspace.getLoadedProtein();
    const loginUrl = loginUrlForMapCase(myStructure);

    /**
     * @param {string} content - The message text
     * @param {Object} [options]
     * @param {number} [options.id] - The message's id; the next one when absent
     * @param {Object} [options.clarification] - A Gibbs question, shown as a card of its options
     * @param {{text: string, name: string}} [options.file] - Text an action returned (an export),
     *   shown with Copy and Download
     */
    const addBotMessage = (content, { id: idIn, clarification, file }={}) => {
        setMessages((oldMessages) => {
            const id = idIn ?? oldMessages.length;
            const newMessage = {
                role: 'assistant', content, id, clarification, file,
            };
            return [...oldMessages, newMessage];
        });
    };

    // The planner is lab-only for now. Without it, chat keeps the conversational route it has.
    const usePlanner = !!Loader.AllowLabFeatures;

    // The message whose question is waiting for an answer. Only its card takes one: a card is
    // closed once answered, when a newer question replaces it, or when a typed request supersedes
    // it (the planner treats any confirmation still open as stale on a new request).
    const [openQuestionId, setOpenQuestionId] = useState(null);

    /**
     * Put the planner's question to the user as a card of options. The message text keeps the
     * question and its options, for the chat history and the downloads.
     */
    const askClarification = (clarification, id) => {
        const labels = (clarification.options || []).map((o) => o.label).filter(Boolean);
        const question = clarification.question || 'Which did you mean?';
        const asList = labels.map((label) => `- ${label}`).join('\n');
        const content = labels.length > 0 ? `${question}\n\n${asList}` : question;
        setOpenQuestionId(id);
        addBotMessage(content, { id, clarification: { ...clarification, question } });
    };

    /**
     * Show what the planner came back with, and say whether it answered. A 'chat' tagged 'llm'
     * is not the planner's own answer, so the conversational route takes it instead; so does
     * anything the planner could not turn into a plan.
     */
    const showPlannerResult = (result, id) => {
        switch (result.type) {
            case 'chat':
                if (result.chatSource === 'llm') return false;
                addBotMessage(result.message, { id });
                return true;
            case 'clarification':
                askClarification(result.clarification, id);
                return true;
            case 'workflow_run': {
                // Its steps have been said as they ran (onStep); this is how the run ended.
                const { completed, errors = [] } = result.execution || {};
                const steps = result.plan.actions.length;
                if (completed) addBotMessage(`Done: all ${steps} steps ran.`);
                else if (errors.length > 0) addBotMessage(`Stopped: ${errors.join('\n')}`);
                else addBotMessage('Stopped after that step; no later step ran.');
                return true;
            }
            case 'plan': {
                const execution = result.execution || {};
                const errors = execution.errors ?? (execution.error ? [execution.error] : []);
                // Said by name, with the values it ran with ("Show Hydrogen Bonds (mode: none)").
                const ran = (execution.results || [])
                    .map((r) => r.displayText || r.actionName || r.actionId).filter(Boolean);
                // Text an action returned for the user to keep (an export) comes with the reply.
                const file = (execution.results || []).find((r) => r.file)?.file;
                // A plan with a message runs a dialogFallback, and the message says for what.
                if (errors.length > 0) {
                    addBotMessage([result.message, ...errors].filter(Boolean).join('\n'), { id });
                } else if (result.message) {
                    addBotMessage(result.message, { id });
                } else {
                    const done = ran.length > 0 ? `Done: ${ran.join(', ')}` : 'Done.';
                    addBotMessage(done, { id, file });
                }
                return true;
            }
            case 'cancelled':
                return true;
            default:
                return false;
        }
    };

    /**
     * The planner's turn for a typed message, which is always a new request: a question is
     * answered on its card, never by guessing from what was typed. False means the planner did not
     * answer and the conversational route should -- because the planner is off, because it
     * declined, or because it failed. The caller does not distinguish them.
     */
    const handleWithPlanner = async (message, id) => {
        if (!usePlanner) return false;
        setOpenQuestionId(null);
        try {
            const answered = showPlannerResult(await interpretText(message), id);
            if (answered) setError(null);
            else logGibbs('handed to the chat model', { text: message });
            return answered;
        } catch (plannerError) {
            console.error('Planner failed; answering with chat', plannerError);
            return false;
        }
    };

    /**
     * Say each workflow step as it starts, with the values it runs with, and how it ended: what it
     * produced, any text it returned (an export), and problems.
     */
    const onStep = ({
        index, total, name, status, said, error: stepError, summary, file, warnings,
    }) => {
        if (status === 'started') {
            setWorkflowRunning(true);
            addBotMessage(`Step ${index + 1} of ${total}: ${name}${said ? ` (${said})` : ''}…`);
        } else if (status === 'failed') {
            addBotMessage(`${name} failed: ${stepError}`);
        } else {
            const problems = warnings?.length > 0 ? `with problems: ${warnings.join(' ')}` : '';
            const options = file ? { file } : {};
            if (summary && problems) addBotMessage(`${name} finished: ${summary}; ${problems}`, options);
            else if (summary) addBotMessage(`${name} finished: ${summary}`, options);
            else if (problems) addBotMessage(`${name} finished, ${problems}`, options);
            else if (file) addBotMessage(`${name} finished.`, options);
        }
    };

    /**
     * Answer the open question with what was chosen on its card: an option's `{ value }`, or
     * `{ input }` typed into a typed-answer option. What the user chose is shown as their message.
     * Only the open question's card is enabled, so only it can answer; and like a typed message,
     * an answer waits while a turn is running. If the answer fails, the card opens again so the
     * user can choose again. Focus goes back to the message field, which a card whose only answer
     * is typed then takes for its own field.
     */
    const handleAnswer = async (questionId, clarification, answer, said) => {
        if (isLoading) return;
        setOpenQuestionId(null);
        const id = messages.length;
        setMessages([...messages, { role: 'user', content: said, id }]);
        setIsLoading(true);
        try {
            const result = await resumeInterpretText(clarification.context, answer, { onStep });
            showPlannerResult(result, id + 1);
            setError(null);
        } catch (answerError) {
            console.error('Error answering a Gibbs question:', answerError);
            setError(answerError);
            setOpenQuestionId(questionId);
        } finally {
            setWorkflowRunning(false);
            setIsLoading(false);
            messageFieldRef.current?.focus();
        }
    };

    const handleSend = async (message=input) => {
        // With the planner on, only allow one turn at a time. How send is prevented during a turn:
        // * Send button: disabled below
        // * Enter key: here
        // * Voice: here, but see BP2-2857 for a stale state issue
        if (usePlanner && isLoading) return;
        try {
            if (message.trim()) {
                // Recorded before sending, so a message that fails is still one Up away.
                chatInputHistory.record(message);
                resetRecall();
                setIsLoading(true);
                const userMessage = { role: 'user', content: message, id: messages.length };
                const updatedMessages = [...messages, userMessage];
                setMessages(updatedMessages);
                setInput('');
                if (await handleWithPlanner(message, updatedMessages.length)) return;
                await submitMessages(updatedMessages);
            }
        } catch (sendError) {
            console.error('Error making chat agent request:', sendError);
            setError(sendError);
        } finally {
            setIsLoading(false);
        }
    };

    const submitMessages = async (sendingMessages) => {
        // A question's card data stays in the browser: the chat endpoint gets its text.
        const data = await WebServices.startWsRequestJson(WebServices.CHAT_URL, {
            messages: sendingMessages.map(({ role, content, id }) => ({ role, content, id })),
        });
        const responseData = JSON.parse(data);
        if (responseData.error === '') {
            setError(null);
            addBotMessage(responseData.response, { id: sendingMessages.length });
        } else {
            console.error(`ChatBot error: ${responseData.error}`);
            setError(responseData.error);
        }
    };

    const handleInputChange = (e) => {
        setInput(e.target.value);
    };

    const handleKeyDown = (e) => {
        onHistoryKeyDown(e);
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleClearChat = () => {
        setOpenQuestionId(null);
        setError(null);
        setMessages([]);
    };

    const handleRegenerate = () => {
        const lastMessage = messages[messages.length - 1];
        const userText = lastMessage.content;
        resetRecall();
        setInput(userText);
        setError(null);
    };

    function displayChatMessage(inputText) {
        const renderer = new marked.Renderer();
        function convertLink(href, title, text) {
            const link = marked.Renderer.prototype.link.call(this, href, title, text);
            // Our ExternalLink also includes a rel="noreferrer" attribute, which excludes
            // the referrer (BMaps) from the request headers.
            // I chose not to add that here. If we decide to add it, we'll need to pass
            // an allowedAttributes option to sanitizeHtml, so rel isn't filtered out.
            return link.replace('<a ', '<a target="_blank" ');
        }
        renderer.link = convertLink;
        marked.setOptions({ renderer });
        return (
            <div
                dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(marked.parse(inputText), {
                        allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img']),
                        allowedAttributes: {
                            ...sanitizeHtml.defaults.allowedAttributes,
                            img: ['src', 'alt', 'title'],
                        },
                        allowedSchemes: ['https'],
                    }),
                }}
            />
        );
    }

    async function captureAndSend() {
        setRecording(true);
        const recorder = await captureAndTranscribe({
            setMediaRecorder,
            handleTranscription: async (words) => {
                if (words != null && words.length > 0) {
                    resetRecall();
                    setInput(words);
                    handleSend(words);
                } else {
                    addBotMessage("If you just said something, I couldn't make it out. Please try again.");
                }
            },
        });
        recorder.onstop = () => setRecording(false);
        setMediaRecorder(recorder);
    }

    async function stopRecording() {
        mediaRecorder.stop();
    }

    return (
        <div style={{
            padding: '1rem',
            paddingTop: '0.5rem',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: '300px',
            flexGrow: 1,
            borderTop: '2px solid lightgray',
        }}
        >
            <Typography style={{ fontWeight: 'bold', fontSize: 'smaller' }}>
                Chat with
                {' '}
                {BotName}
                , our AI assistant:
            </Typography>
            <Typography style={{ fontSize: 'small', marginBottom: '0.5rem' }}>
                Please be aware responses from
                {' '}
                {BotName}
                {' '}
                may not always be accurate.
                Use discretion when interpreting results.
            </Typography>
            {/* A log, so a screen reader announces new replies and questions while focus stays
                in the message field. */}
            <div
                role="log"
                aria-live="polite"
                aria-label={`Conversation with ${BotName}`}
                style={{
                    flexGrow: '1',
                    overflow: 'auto',
                    padding: '0.5rem',
                    borderTop: '1px solid lightgray',
                    borderLeft: '1px solid lightgray',
                    borderRight: '1px solid lightgray',
                    borderBottom: 'none',
                    borderRadius: '4px 4px 0 0',
                    background: 'whitesmoke',
                }}
            >
                {messages.map((message, messageI) => (
                    <Box
                        key={`message_${messageI.toString()}`}
                        ref={message.id === messages.length - 1 ? messagesEndRef : null}
                        style={{
                            marginBottom: '2px',
                            padding: '2px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
                        }}
                    >
                        <strong>{message.role === 'user' ? 'You: ' : `${BotName}: `}</strong>
                        <Box sx={{
                            border: '1px solid lightgray',
                            borderRadius: '.3rem',
                            maxWidth: '90%',
                            wordWrap: 'break-word',
                            background: message.role === 'user' ? 'white' : 'mintcream',
                            boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2)',
                            '& li': {
                                marginLeft: '1rem',
                            },
                            '& div': {
                                padding: '.3rem',
                            },
                            '& pre': {
                                whiteSpace: 'pre-wrap',
                            },
                            '& img': {
                                maxWidth: '100%',
                                height: 'auto',
                            },
                        }}
                        >
                            {message.clarification ? (
                                <>
                                    <div id={`gibbs-question-${message.id}`}>
                                        {displayChatMessage(message.clarification.question)}
                                    </div>
                                    <QuestionCard
                                        clarification={message.clarification}
                                        labelledBy={`gibbs-question-${message.id}`}
                                        open={message.id === openQuestionId}
                                        onAnswer={(answer, said) => handleAnswer(
                                            message.id, message.clarification, answer, said,
                                        )}
                                    />
                                </>
                            ) : (
                                <>
                                    {displayChatMessage(message.content)}
                                    {message.file && <ReturnedText file={message.file} />}
                                </>
                            )}
                        </Box>
                    </Box>
                ))}
                <Box>
                    {isLoading && (
                        <Box>
                            <WorkingIndicator />
                        </Box>
                    )}
                    {workflowRunning && (
                        // A step under way finishes on its own; no later step starts.
                        <DialogCancelButton onClick={() => stopWorkflow()}>
                            Stop after this step
                        </DialogCancelButton>
                    )}
                </Box>
                {error && <ChatBotErrorDisplay error={error} onTryAgain={handleRegenerate} />}
                <Box>
                    {!loggedIn && (
                        <Typography style={{ fontStyle: 'italic', fontWeight: 'bold', fontSize: 'smaller' }}>
                            Please
                            {' '}
                            <a href={loginUrl}>Log in</a>
                            {' '}
                            to interact with
                            {' '}
                            {BotName}
                            .
                        </Typography>
                    )}
                </Box>
            </div>
            <TextField
                style={{
                    marginBottom: '0.5rem',
                    width: '100%',
                    flexGrow: 1,
                    justifyContent: 'flex-end',
                    borderLeft: '1px solid lightgray',
                    borderRight: '1px solid lightgray',
                    borderRadius: '0 0 4px 4px',
                    background: 'whitesmoke',
                }}
                inputRef={messageFieldRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Enter message..."
                multiline
                maxRows={6}
                size="small"
                disabled={!loggedIn}
                slotProps={{
                    input: {
                        sx: {
                            background: 'white',
                        },
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={recording ? stopRecording : captureAndSend}
                                    style={{
                                        color: recording ? 'green' : undefined,
                                        padding: '2px 4px',
                                        height: 'fit-content',
                                        width: 'fit-content',
                                        display: 'flex',
                                    }}
                                    title={recording ? 'Disable voice control' : 'Enable voice control'}
                                    disabled={!loggedIn}
                                >
                                    <Mic />
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <DialogSubmitButton
                    onClick={() => handleSend()}
                    disabled={!loggedIn || (usePlanner && isLoading)}
                >
                    Send
                </DialogSubmitButton>
                <DialogCancelButton
                    onClick={handleClearChat}
                    disabled={!loggedIn}
                >
                    Clear chat
                </DialogCancelButton>
                <ChatMenu messages={messages} />
            </div>
        </div>
    );
}

/**
 * A Gibbs question's options, answered by click or keyboard: each option is a button (Tab to it,
 * Enter or Space to choose), and an option marked `input` is a labelled text field answered with
 * Enter or its Go button. The options are a group named by the question, for screen readers.
 * Only the open question's card takes an answer; the others stay visible, disabled.
 *
 * @param {Object} props
 * @param {Object} props.clarification - The planner's question: `options` of `{label, value}`,
 *   or `{label, input: true}` for a typed answer
 * @param {string} props.labelledBy - Id of the element showing the question
 * @param {boolean} props.open - Whether this is the question waiting for an answer
 * @param {(answer: ({value: *}|{input: string}), said: string) => void} props.onAnswer - Called
 *   with the chosen option's `{ value }`, or `{ input }` typed into a field, and what to show as
 *   the user's message
 */
function QuestionCard({
    clarification, labelledBy, open, onAnswer,
}) {
    const [typed, setTyped] = useState({});
    const options = clarification.options || [];
    // A card that only asks for a typed value takes focus, since typing is the only answer.
    const onlyTyped = options.length === 1 && options[0].input;

    const submitTyped = (option) => {
        const text = (typed[option.label] || '').trim();
        if (open && text) onAnswer({ input: text }, text);
    };

    if (options.length === 0) return null;
    return (
        <Box
            role="group"
            aria-labelledby={labelledBy}
            sx={{
                display: 'flex', flexWrap: 'wrap', gap: '.3rem',
            }}
        >
            {options.map((option) => (option.input ? (
                <Box
                    key={option.label}
                    sx={{
                        display: 'flex', alignItems: 'center', gap: '.3rem', width: '100%',
                    }}
                >
                    <TextField
                        size="small"
                        placeholder={option.label}
                        value={typed[option.label] || ''}
                        onChange={(e) => setTyped(
                            (prev) => ({ ...prev, [option.label]: e.target.value })
                        )}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                submitTyped(option);
                            }
                        }}
                        disabled={!open}
                        autoFocus={open && onlyTyped}
                        slotProps={{ htmlInput: { 'aria-label': option.label } }}
                        sx={{ flex: 1, background: 'white' }}
                    />
                    <DialogSubmitButton
                        size="small"
                        aria-label={`Send: ${option.label}`}
                        onClick={() => submitTyped(option)}
                        disabled={!open}
                    >
                        Go
                    </DialogSubmitButton>
                </Box>
            ) : (
                <Chip
                    key={option.label}
                    label={option.label}
                    onClick={() => onAnswer({ value: option.value }, option.label)}
                    disabled={!open}
                    sx={{
                        // A long label is cut short rather than widening the chat.
                        maxWidth: '100%',
                        '& .MuiChip-label': {
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        },
                        '&.Mui-focusVisible': { outline: '2px solid', outlineOffset: '2px' },
                    }}
                />
            )))}
        </Box>
    );
}

function ChatBotErrorDisplay({ error, onTryAgain }) {
    const errorRef = useRef(null);
    useEffect(() => {
        if (errorRef?.current) {
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [error]);

    return (
        <Box ref={errorRef}>
            <strong>
                {`${BotName}: `}
            </strong>
            <BMapsTooltip title={`Error detail: ${error}`}>
                <div style={{ fontStyle: 'italic', color: 'red' }}>
                    Something went wrong...
                    {' '}
                    <InfoIcon fontSize="small" style={{ verticalAlign: 'middle' }} />
                </div>
            </BMapsTooltip>
            <DialogCancelButton style={{ paddingTop: 0, paddingBottom: 0 }} fontSize="smaller" type="button" onClick={onTryAgain}>Try again</DialogCancelButton>
        </Box>
    );
}

function ChatMenu({ messages }) {
    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);
    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {messages.length > 0 && (
                <>
                    <BMapsTooltip title="Download chat history">
                        <IconButton size="small" ref={menuRef} onClick={() => setOpen(!open)}>
                            <GetAppIcon />
                        </IconButton>
                    </BMapsTooltip>
                    <Popper
                        open={open}
                        anchorEl={menuRef.current}
                        placement="bottom-start"
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={() => setOpen(false)}>
                                <MenuList>
                                    <MenuItem
                                        sx={{ justifyContent: 'center' }}
                                        onClick={
                                    () => {
                                        const stateString = JSON.stringify(
                                            { chatMessages: messages }
                                        );
                                        downloadData(getChatFileName('json'), stateString, 'application/json');
                                        setOpen(false);
                                    }
                                }
                                    >
                                        JSON
                                    </MenuItem>
                                    <MenuItem
                                        sx={{ justifyContent: 'end' }}
                                        onClick={
                                        () => {
                                            const stateString = messages.map((message) => `${message.role}:\n${message.content}`).join('\n\n');
                                            downloadData(getChatFileName('md'), stateString, 'text/plain');
                                            setOpen(false);
                                        }
                                    }
                                    >
                                        Markdown
                                    </MenuItem>
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Popper>
                </>
            )}
        </Box>
    );
}

function getChatFileName(format) {
    return `BMaps_GibbsChat_${formatDate()}.${format}`;
}

/**
 * @param {Date?} dateIn
 * @returns {string}
 */
function formatDate(dateIn) {
    const date = dateIn || new Date();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    // const hours = date.getHours();
    // const minutes = date.getMinutes();
    // const seconds = date.getSeconds();
    return `${year}-${month}-${day}`;
}
